import { auth } from "./config";

const keyFor = (kind, eventId, resource) => `parlipro-cap:${kind}:${eventId}:${resource}`;

function saveRedemption(redemption) {
  if (typeof sessionStorage === "undefined") return;
  if (redemption?.eventId && auth.currentUser?.uid && Array.isArray(redemption.matchedOfficialNames)) {
    sessionStorage.setItem(
      keyFor("officialMatches", redemption.eventId, auth.currentUser.uid),
      JSON.stringify(redemption.matchedOfficialNames)
    );
  }
  if (redemption?.displayCodes && redemption.eventId) {
    const resource = redemption.boutId || `${redemption.disciplineId}:${redemption.poolName}`;
    sessionStorage.setItem(keyFor("display", redemption.eventId, resource), JSON.stringify(redemption.displayCodes));
  }
  if (!redemption?.token) return;
  if (redemption.type === "bout") {
    sessionStorage.setItem(keyFor("boutJudge", redemption.eventId, redemption.boutId), redemption.token);
  } else if (redemption.type === "boutRef") {
    sessionStorage.setItem(keyFor("boutRef", redemption.eventId, redemption.boutId), redemption.token);
  } else if (redemption.type === "boutOfficial") {
    sessionStorage.setItem(keyFor("boutOfficial", redemption.eventId, redemption.boutId), redemption.token);
  } else if (redemption.type === "pool") {
    const resource = `${redemption.disciplineId}:${redemption.poolName}`;
    sessionStorage.setItem(keyFor("pool", redemption.eventId, resource), redemption.token);
    if (redemption.poolId) sessionStorage.setItem(keyFor("poolId", redemption.eventId, resource), redemption.poolId);
    // A pool referee follows links into individual bout routes. This event-scoped
    // pointer lets those routes carry the same capability without putting it in a URL.
    sessionStorage.setItem(keyFor("poolCurrent", redemption.eventId, "ref"), redemption.token);
  }
}

export function getRedeemedDisplayCodes(eventId, resource) {
  if (typeof sessionStorage === "undefined") return {};
  try { return JSON.parse(sessionStorage.getItem(keyFor("display", eventId, resource)) || "{}"); }
  catch { return {}; }
}

export function getMatchedOfficialNames(eventId) {
  if (typeof sessionStorage === "undefined" || !auth.currentUser?.uid) return [];
  try {
    return JSON.parse(sessionStorage.getItem(keyFor("officialMatches", eventId, auth.currentUser.uid)) || "[]");
  } catch {
    return [];
  }
}

const ownerCodeCache = new Map();

export async function getOwnerEventCodes(eventId) {
  const user = auth.currentUser;
  if (!user) return [];
  const cached = ownerCodeCache.get(eventId);
  if (cached?.uid === user.uid) return cached.promise;
  let promise;
  promise = (async () => {
    const response = await fetch(`/api/eventAccessCodes?eventId=${encodeURIComponent(eventId)}`, {
      headers: { Authorization: `Bearer ${await user.getIdToken()}` },
    });
    if (!response.ok) throw new Error("Unable to load event access codes");
    return (await response.json()).codes || [];
  })().catch(() => {
    // Do not pin a transient API/auth failure in the session cache. The next
    // snapshot can retry while the rest of the event continues rendering.
    if (ownerCodeCache.get(eventId)?.promise === promise) ownerCodeCache.delete(eventId);
    return [];
  });
  ownerCodeCache.set(eventId, { uid: user.uid, promise });
  return promise;
}

export function invalidateOwnerEventCodes(eventId) {
  ownerCodeCache.delete(eventId);
}

export async function createOwnerResources(operation, body) {
  if (!auth.currentUser) throw new Error("Authentication required");
  const response = await fetch("/api/createResources", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await auth.currentUser.getIdToken()}`,
    },
    body: JSON.stringify({ ...body, operation }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || "Unable to create resources");
  if (body.eventId) invalidateOwnerEventCodes(body.eventId);
  return payload;
}

export async function redeemAccessCode(code) {
  const headers = { "Content-Type": "application/json" };
  if (auth.currentUser) headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
  const response = await fetch("/api/redeemAccessCode", {
    method: "POST",
    headers,
    body: JSON.stringify({ code }),
  });
  if (response.status === 404) return null;
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || "Unable to redeem code");
  saveRedemption(payload);
  return payload;
}

export function getPoolAccessToken(eventId, disciplineId, poolName) {
  if (typeof sessionStorage === "undefined") return null;
  return sessionStorage.getItem(keyFor("pool", eventId, `${disciplineId}:${poolName}`));
}

export function getPoolAccessPoolId(eventId, disciplineId, poolName) {
  if (typeof sessionStorage === "undefined") return null;
  return sessionStorage.getItem(keyFor("poolId", eventId, `${disciplineId}:${poolName}`));
}

function getCommandToken(command, body) {
  if (typeof sessionStorage === "undefined") return null;
  const judgeCommands = new Set(["claimJudgeSeat", "submitJudgeRound", "editJudgeRound", "updateLiveTaps", "submitJudgeFinal"]);
  if (judgeCommands.has(command)) {
    return sessionStorage.getItem(keyFor("boutJudge", body.eventId, body.boutId))
      || sessionStorage.getItem(keyFor("boutOfficial", body.eventId, body.boutId))
      || sessionStorage.getItem(keyFor("poolCurrent", body.eventId, "ref"));
  }
  if (["claimPoolSeat", "releasePoolSeat", "startNextPoolBout", "confirmPoolResults"].includes(command)) {
    return getPoolAccessToken(body.eventId, body.disciplineId, body.poolName);
  }
  return sessionStorage.getItem(keyFor("boutRef", body.eventId, body.boutId))
    || sessionStorage.getItem(keyFor("boutOfficial", body.eventId, body.boutId))
    || sessionStorage.getItem(keyFor("poolCurrent", body.eventId, "ref"))
    || sessionStorage.getItem(keyFor("boutJudge", body.eventId, body.boutId));
}

export async function sendAccessCommand(command, body) {
  const token = getCommandToken(command, body);
  if (!token) return null;
  const headers = { "Content-Type": "application/json", "X-Access-Token": token };
  if (auth.currentUser) headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
  const response = await fetch("/api/accessCommand", {
    method: "POST",
    headers,
    body: JSON.stringify({ command, ...body }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || "Unable to update scoring state");
  return payload;
}
